import { supabase } from '@/lib/supabaseClient';
import { withRetry } from '@/utils/retry';

// Supabase chat message type
export type ChatMessage = {
  id: string; // UUID
  created_at: string; // ISO timestamp
  chat_id: string; // UUID
  user_id: string;
  user_name: string;
  content: string;
  created_at_ms: number; // timestamp in milliseconds (derived)
  updated_at_ms: number; // timestamp in milliseconds (derived)
};

// Supabase typing indicator type
export type TypingIndicator = {
  id: string; // UUID
  created_at: string; // ISO timestamp
  chat_id: string; // UUID
  user_id: string;
  user_name: string;
  started_at: number; // timestamp in milliseconds
};

// Supabase row type for chat messages
interface ChatMessageRow {
  id: string;
  created_at: string;
  chat_id: string;
  user_id: string;
  user_name: string;
  content: string;
  updated_at?: string;
  created_at_ms?: number;
  updated_at_ms?: number;
}

// Supabase row type for typing indicators
interface TypingIndicatorRow {
  id: string;
  created_at: string;
  chat_id: string;
  user_id: string;
  user_name: string;
  started_at: number;
}

// Helper to convert Supabase message to ChatMessage format
function convertMessageFromSupabase(row: ChatMessageRow): ChatMessage & { id: string } {
  const createdAtMs = row.created_at ? new Date(row.created_at).getTime() : row.created_at_ms || Date.now();
  const updatedAtMs = row.updated_at ? new Date(row.updated_at).getTime() : row.updated_at_ms || createdAtMs;

  return {
    ...row,
    id: row.id,
    created_at_ms: createdAtMs,
    updated_at_ms: updatedAtMs,
  } as ChatMessage & { id: string };
}

// Helper to convert Supabase typing indicator
function convertTypingFromSupabase(row: TypingIndicatorRow): TypingIndicator & { id: string } {
  return {
    ...row,
    id: row.id,
  } as TypingIndicator & { id: string };
}

// Message CRUD operations
export async function getChatMessages(chatId: string): Promise<Array<ChatMessage & { id: string }>> {
  const { data: messages, error } = await withRetry(() =>
    supabase
      .from('chat_messages')
      .select('*')
      .eq('chat_id', chatId)
      .order('created_at', { ascending: true })
  );

  if (error) throw error;
  return (messages || []).map(convertMessageFromSupabase);
}

export async function sendChatMessage(
  chatId: string,
  userId: string,
  userName: string,
  content: string
): Promise<(ChatMessage & { id: string }) | null> {
  const now = Date.now();
  const { data: message, error } = await withRetry(() =>
    supabase
      .from('chat_messages')
      .insert({
        chat_id: chatId,
        user_id: userId,
        user_name: userName,
        content,
        created_at: new Date(now).toISOString(),
        updated_at: new Date(now).toISOString(),
      })
      .select()
      .single()
  );

  if (error || !message) return null;

  // Update chat updated_at
  await withRetry(() =>
    supabase
      .from('spaces')
      .update({ updated_at: new Date(now).toISOString() })
      .eq('id', chatId)
  );

  return convertMessageFromSupabase(message);
}

export async function deleteChatMessage(messageId: string): Promise<boolean> {
  const { error } = await withRetry(() =>
    supabase
      .from('chat_messages')
      .delete()
      .eq('id', messageId)
  );
  
  if (error) throw error;
  return true;
}

// Typing indicators
export async function setTypingIndicator(
  chatId: string,
  userId: string,
  userName: string
): Promise<void> {
  const now = Date.now();

  // Check if indicator already exists
  const { data: existing } = await withRetry(() =>
    supabase
      .from('chat_typing')
      .select('id')
      .eq('chat_id', chatId)
      .eq('user_id', userId)
      .single()
  );

  if (existing) {
    // Update existing
    await withRetry(() =>
      supabase
        .from('chat_typing')
        .update({ started_at: now })
        .eq('id', existing.id)
    );
  } else {
    // Create new
    await withRetry(() =>
      supabase
        .from('chat_typing')
        .insert({
          chat_id: chatId,
          user_id: userId,
          user_name: userName,
          started_at: now,
        })
    );
  }
}

export async function removeTypingIndicator(
  chatId: string,
  userId: string
): Promise<void> {
  await withRetry(() =>
    supabase
      .from('chat_typing')
      .delete()
      .eq('chat_id', chatId)
      .eq('user_id', userId)
  );
}

export async function getTypingIndicators(chatId: string): Promise<Array<TypingIndicator & { id: string }>> {
  const tenSecondsAgo = Date.now() - 10000;
  const { data: indicators, error } = await withRetry(() =>
    supabase
      .from('chat_typing')
      .select('*')
      .eq('chat_id', chatId)
      .gte('started_at', tenSecondsAgo)
  );

  if (error) throw error;
  return (indicators || []).map(convertTypingFromSupabase);
}

// Realtime subscription helper using Supabase Realtime
export function subscribeToChatMessages(
  chatId: string,
  onNewMessage: (message: ChatMessage & { id: string }) => void,
  onDeleteMessage?: (messageId: string) => void
) {
  const channel = supabase
    .channel(`chat:${chatId}`)
    .on(
      'postgres_changes',
      {
        event: 'INSERT',
        schema: 'public',
        table: 'chat_messages',
        filter: `chat_id=eq.${chatId}`,
      },
      (payload) => {
        const message = convertMessageFromSupabase(payload.new as ChatMessageRow);
        onNewMessage(message);
      }
    )
    .on(
      'postgres_changes',
      {
        event: 'DELETE',
        schema: 'public',
        table: 'chat_messages',
        filter: `chat_id=eq.${chatId}`,
      },
      (payload) => {
        if (onDeleteMessage) {
          onDeleteMessage(payload.old.id);
        }
      }
    )
    .subscribe();

  return () => {
    supabase.removeChannel(channel);
  };
}

export function subscribeToTypingIndicators(
  chatId: string,
  currentUserId: string,
  onTypingChange: (typingUsers: Array<TypingIndicator & { id: string }>) => void
) {
  let debounceTimer: ReturnType<typeof setTimeout>;
  
  const channel = supabase
    .channel(`chat-typing:${chatId}`)
    .on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'chat_typing',
        filter: `chat_id=eq.${chatId}`,
      },
      async () => {
        // Debounce typing indicator updates to avoid excessive API calls
        clearTimeout(debounceTimer);
        debounceTimer = setTimeout(async () => {
          const indicators = await getTypingIndicators(chatId);
          const filtered = indicators.filter(i => i.user_id !== currentUserId);
          onTypingChange(filtered);
        }, 150);
      }
    )
    .subscribe();

  return () => {
    clearTimeout(debounceTimer);
    supabase.removeChannel(channel);
  };
}
